<template>
  <v-card dark class="mx-auto py-3">
    <v-tabs v-model="viewTab" dark background-color="transparent" class="mb-4 px-4" color="#00963f">
      <v-tab><v-icon left>mdi-plus-circle-outline</v-icon>Abwesenheit erfassen</v-tab>
      <v-tab><v-icon left>mdi-file-document-edit-outline</v-icon>Einträge verwalten</v-tab>
    </v-tabs>

    <!-- Tab 1: Abwesenheit erfassen -->
    <v-window v-model="viewTab">
      <v-window-item>
        <v-row v-if="loading">
          <v-col cols="12">
            <v-progress-linear class="my-5 px-5" indeterminate color="primary"></v-progress-linear>
          </v-col>
        </v-row>
        <v-row v-else>
          <v-col cols="12">
            <v-stepper v-model="stepper" vertical>
              <v-stepper-step step="1" :complete="stepper > 1">Auswahl</v-stepper-step>

              <v-stepper-content step="1">
                <user-search class="pt-0 ma-5" :value="sicko" ignoreRights label="Mitarbeiter" @update="updateSicko"></user-search>
                <div class="d-flex align-center mt-4">
                  <v-btn color="primary" class="px-6" @click="stepper++">Weiter</v-btn>
                </div>
              </v-stepper-content>

              <v-stepper-step step="2" :complete="stepper > 2">
                Typ auswählen
                <span class="orange--text" v-if="type"> ({{ typeItems.find((item) => item.value === type).text }}) </span>
              </v-stepper-step>

              <v-stepper-content step="2">
                <v-select label="Abwesenheitstyp auswählen" v-model="type" :type-items="typeItems" :items="typeItems"></v-select>

                <v-expand-transition>
                  <div v-if="type === 'school'" class="my-4 pa-4 rounded-xl border-light" style="background: rgba(255, 255, 255, 0.02)">
                    <div class="text-subtitle-2 white--text font-weight-bold mb-3 d-flex align-center">
                      <v-icon color="#00963f" class="mr-2" small>mdi-auto-fix</v-icon>
                      Schultage für einen Monat automatisch vorschlagen
                    </div>
                    <v-row dense align="center">
                      <v-col cols="12" sm="5">
                        <v-select v-model="suggestMonth" :items="monthItems" label="Monat" outlined dense hide-details class="rounded-lg"></v-select>
                      </v-col>
                      <v-col cols="12" sm="4">
                        <v-select v-model="suggestYear" :items="yearItems" label="Jahr" outlined dense hide-details class="rounded-lg"></v-select>
                      </v-col>
                      <v-col cols="12" sm="3">
                        <v-btn
                          color="primary"
                          block
                          outlined
                          :loading="suggestLoading"
                          @click="fetchSchoolDaysSuggestions"
                          class="font-weight-bold"
                          style="height: 40px">
                          Vorschlagen
                        </v-btn>
                      </v-col>
                    </v-row>
                  </div>
                </v-expand-transition>

                <v-text-field
                  v-if="type === 'other'"
                  label="Grund für Abwesenheit (Pflichtfeld)"
                  v-model="note"
                  :rules="rules.required"></v-text-field>
                <v-checkbox
                  v-if="type === 'sick'"
                  v-model="requiresEmployeeApproval"
                  label="Mitarbeiter-Freigabe erforderlich (z.B. für Arztzeugnis)"
                  color="primary"
                  hide-details
                  class="mt-2 mb-4"></v-checkbox>
                <div class="d-flex align-center mt-6">
                  <v-btn text @click="stepper--" class="mr-4 grey--text text--lighten-1">Zurück</v-btn>
                  <v-btn color="primary" class="px-6" @click="weiterSchritt2">Weiter</v-btn>
                </div>
              </v-stepper-content>

              <v-stepper-step step="3" :complete="stepper > 3">Tage auswählen</v-stepper-step>

              <v-stepper-content step="3">
                <v-form ref="form" v-show="stepper === 3">
                  <!-- Liste der bereits erfassten Tage -->
                  <div v-if="sickDays && sickDays.length > 0" class="mb-4">
                    <div class="grey--text text-caption font-weight-bold mb-2">Hinzugefügte Abwesenheitstage:</div>
                    <v-card
                      v-for="(sickDay, index) of sickDays"
                      :key="index"
                      color="rgba(255,255,255,0.03)"
                      class="rounded-xl mb-3 border-light elevation-0"
                      :style="'border-left: 4px solid ' + getBorderColor(type) + ' !important;'">
                      <v-card-text class="pa-4">
                        <v-row dense class="align-center mx-0">
                          <v-col cols="12" sm="5">
                            <v-menu
                              v-model="sickDays[index].datePickerMenu"
                              :close-on-content-click="false"
                              :nudge-right="40"
                              transition="scale-transition"
                              offset-y
                              min-width="auto">
                              <template v-slot:activator="{ on, attrs }">
                                <v-text-field
                                  :rules="rules.required"
                                  v-model="sickDays[index].date"
                                  label="Datum"
                                  readonly
                                  outlined
                                  dense
                                  hide-details
                                  prepend-inner-icon="mdi-calendar"
                                  class="rounded-lg"
                                  v-bind="attrs"
                                  v-on="on"></v-text-field>
                              </template>
                              <v-date-picker
                                first-day-of-week="1"
                                locale="de-DE"
                                v-model="sickDays[index].date"
                                @input="onSickDayDateChanged(index, $event)"></v-date-picker>
                            </v-menu>
                          </v-col>
                          <v-col cols="9" sm="5" class="d-flex align-center">
                            <v-text-field
                              label="Stunden"
                              v-model="sickDays[index].hours"
                              outlined
                              dense
                              hide-details
                              prepend-inner-icon="mdi-clock-outline"
                              class="rounded-lg flex-grow-1"></v-text-field>
                          </v-col>
                          <v-col cols="3" sm="2" class="text-right">
                            <v-btn icon color="error" class="glass-btn" @click="sickDays = sickDays.filter((day, sickIndex) => sickIndex !== index)">
                              <v-icon>mdi-trash-can-outline</v-icon>
                            </v-btn>
                          </v-col>
                        </v-row>

                        <v-row dense class="align-center mx-0 mt-2" v-if="type && type !== 'holiday'">
                          <v-col
                            :key="'added-day-pct-' + index"
                            cols="12"
                            :sm="sickDays[index].sickPercentage < 100 ? (sickDays[index].calculationMode === 'proportional' ? 4 : 6) : 12">
                            <v-select
                              :label="getPercentageLabel(type)"
                              v-model="sickDays[index].sickPercentage"
                              :items="getSickPercentageOptions(sickDays[index].sickPercentage)"
                              suffix="%"
                              outlined
                              dense
                              hide-details
                              class="rounded-lg"
                              @change="onAddedDayPercentageChange(index)"></v-select>
                          </v-col>
                          <v-col
                            :key="'added-day-mode-' + index"
                            cols="12"
                            :sm="sickDays[index].calculationMode === 'proportional' ? 4 : 6"
                            v-if="sickDays[index].sickPercentage < 100">
                            <v-select
                              label="Dienstplan-Auswirkung"
                              v-model="sickDays[index].calculationMode"
                              :items="[
                                { value: 'fixed', text: 'Schicht bleibt aktiv (Dienstplan unberührt)' },
                                { value: 'proportional', text: 'Schicht kürzen (anteilig im Dienstplan splitten)' },
                                { value: 'time_window', text: 'Exaktes Zeitfenster im Dienstplan markieren' },
                              ]"
                              outlined
                              dense
                              hide-details
                              class="rounded-lg"></v-select>
                          </v-col>
                          <v-col
                            :key="'added-day-loc-' + index"
                            cols="12"
                            sm="4"
                            v-if="sickDays[index].sickPercentage < 100 && sickDays[index].calculationMode === 'proportional'">
                            <v-select
                              label="Lage der Kürzung"
                              v-model="sickDays[index].sickLocation"
                              :items="[
                                { value: 'start', text: 'Am Anfang der Schicht' },
                                { value: 'end', text: 'Am Ende der Schicht' },
                              ]"
                              outlined
                              dense
                              hide-details
                              class="rounded-lg"></v-select>
                          </v-col>
                          <v-col
                            :key="'added-day-window-' + index"
                            cols="12"
                            v-if="sickDays[index].sickPercentage < 100 && sickDays[index].calculationMode === 'time_window'">
                            <v-text-field
                              v-model="sickDays[index].sickTimeRange"
                              label="Exaktes Zeitfenster"
                              placeholder="z. B. 13:15 - 15:15"
                              hint="Mehrere Fenster mit Komma trennen."
                              persistent-hint
                              outlined
                              dense
                              class="rounded-lg"
                              @change="onAddedDayTimeRangeTextChange(index)"></v-text-field>
                          </v-col>

                          <!-- Erklärung der Verrechnungsart -->
                          <v-col :key="'added-day-desc-' + index" cols="12" class="pt-1 pb-0" v-if="sickDays[index].sickPercentage < 100">
                            <div class="grey--text text-caption" style="line-height: 1.3">
                              <span v-if="sickDays[index].calculationMode === 'fixed'">
                                ℹ️ <strong>Schicht bleibt aktiv:</strong> Der Mitarbeiter bleibt voll im Dienstplan eingeteilt (keine Änderung der
                                Schicht).
                              </span>
                              <span v-if="sickDays[index].calculationMode === 'proportional'">
                                ℹ️ <strong>Schicht kürzen:</strong> Die geplante Schicht wird im Dienstplan anteilig gekürzt (z. B. bei 50% werden 4h
                                gearbeitet und 4h krank gewertet).
                              </span>
                              <span v-if="sickDays[index].calculationMode === 'time_window'">
                                ℹ️ <strong>Exaktes Zeitfenster:</strong> Nur die angegebenen Zeiten werden im Dienstplan als krank markiert.
                              </span>
                            </div>
                          </v-col>
                        </v-row>
                        <!-- Shift Info Pill if found -->
                        <v-expand-transition>
                          <div v-if="type && type !== 'holiday'" class="mt-3 pl-1 d-flex align-center flex-wrap" style="gap: 6px">
                            <v-chip
                              v-if="$moment(sickDay.date).isoWeekday() >= 6"
                              color="rgba(255, 152, 0, 0.12)"
                              class="warning--text font-weight-black px-3 py-1 text-subtitle-2"
                              style="height: auto; border: 1px solid rgba(255, 152, 0, 0.3)">
                              <v-icon small color="orange" class="mr-2">mdi-calendar-weekend</v-icon>
                              Wochenende
                            </v-chip>
                            <v-chip
                              v-if="sickDay.shiftInfo"
                              color="rgba(0, 150, 63, 0.12)"
                              class="primary-green--text font-weight-medium px-3 py-1 text-subtitle-2"
                              style="height: auto; border: 1px solid rgba(0, 150, 63, 0.2)">
                              <v-icon small color="#00963f" class="mr-2">mdi-calendar-check</v-icon>
                              Automatisch ermittelt: <strong class="ml-1">{{ sickDay.shiftInfo.name }}</strong> &nbsp;({{ sickDay.shiftInfo.times }})
                            </v-chip>
                            <v-chip
                              v-else-if="sickDay.percentage !== undefined"
                              color="rgba(255, 152, 0, 0.08)"
                              class="orange--text text--lighten-1 font-weight-medium px-3 py-1 text-subtitle-2"
                              style="height: auto; border: 1px solid rgba(255, 152, 0, 0.2)">
                              <v-icon small color="orange" class="mr-2">mdi-alert-circle-outline</v-icon>
                              Keine Schicht geplant (Stunden manuell)
                            </v-chip>
                          </div>
                          <div v-else-if="sickDay.percentage !== undefined" class="mt-3 pl-1 d-flex align-center flex-wrap" style="gap: 6px">
                            <v-chip
                              v-if="$moment(sickDay.date).isoWeekday() >= 6"
                              color="rgba(255, 152, 0, 0.12)"
                              class="warning--text font-weight-black px-3 py-1 text-subtitle-2"
                              style="height: auto; border: 1px solid rgba(255, 152, 0, 0.3)">
                              <v-icon small color="orange" class="mr-2">mdi-calendar-weekend</v-icon>
                              Wochenende
                            </v-chip>
                            <v-chip
                              color="rgba(0, 150, 63, 0.12)"
                              class="primary-green--text font-weight-medium px-3 py-1 text-subtitle-2"
                              style="height: auto; border: 1px solid rgba(0, 150, 63, 0.2)">
                              <v-icon small color="#00963f" class="mr-2">mdi-percent</v-icon>
                              Pensum: <strong class="ml-1">{{ sickDay.percentage }}%</strong> &nbsp;(Sollstunden: {{ sickDay.targetHours }} h)
                            </v-chip>
                          </div>
                        </v-expand-transition>
                      </v-card-text>
                    </v-card>
                  </div>

                  <!-- Neuen Tag hinzufügen Card -->
                  <div class="grey--text text-caption font-weight-bold mb-2">Weiteren Tag hinzufügen:</div>
                  <v-card
                    color="rgba(0, 150, 63, 0.02)"
                    class="rounded-xl mb-6 border-light elevation-0"
                    :style="
                      'border: 1px dashed ' +
                      getBorderColor(type) +
                      '33 !important; border-left: 4px dashed ' +
                      getBorderColor(type) +
                      'bb !important;'
                    ">
                    <v-card-text class="pa-4">
                      <v-row dense class="align-center mx-0">
                        <v-col cols="12" sm="5">
                          <v-menu
                            v-model="datePickerMenu"
                            :close-on-content-click="false"
                            :nudge-right="40"
                            transition="scale-transition"
                            offset-y
                            min-width="auto">
                            <template v-slot:activator="{ on, attrs }">
                              <v-text-field
                                v-model="date"
                                label="Datum wählen"
                                readonly
                                outlined
                                dense
                                hide-details
                                prepend-inner-icon="mdi-calendar"
                                class="rounded-lg"
                                v-bind="attrs"
                                v-on="on"></v-text-field>
                            </template>
                            <v-date-picker first-day-of-week="1" locale="de-DE" v-model="date" @input="datePickerMenu = false"></v-date-picker>
                          </v-menu>
                        </v-col>
                        <v-col cols="9" sm="5" class="d-flex align-center">
                          <v-text-field
                            label="Stunden"
                            v-model="hours"
                            outlined
                            dense
                            hide-details
                            prepend-inner-icon="mdi-clock-outline"
                            class="rounded-lg flex-grow-1"></v-text-field>
                        </v-col>
                        <v-col cols="3" sm="2" class="text-right">
                          <v-btn icon color="primary" class="glass-btn elevation-2" @click="addSickDay">
                            <v-icon>mdi-plus-box</v-icon>
                          </v-btn>
                        </v-col>
                      </v-row>

                      <v-row dense class="align-center mx-0 mt-3" v-if="type && type !== 'holiday'">
                        <v-col key="main-pct-col" cols="12" :sm="sickPercentage < 100 ? (calculationMode === 'proportional' ? 4 : 6) : 12">
                          <v-select
                            :label="getPercentageLabel(type)"
                            v-model="sickPercentage"
                            :items="getSickPercentageOptions(sickPercentage)"
                            suffix="%"
                            outlined
                            dense
                            hide-details
                            class="rounded-lg"
                            @change="onPercentageChange"></v-select>
                        </v-col>
                        <v-col key="main-mode-col" cols="12" :sm="calculationMode === 'proportional' ? 4 : 6" v-if="sickPercentage < 100">
                          <v-select
                            label="Dienstplan-Auswirkung"
                            v-model="calculationMode"
                            :items="[
                              { value: 'fixed', text: 'Schicht bleibt aktiv (Dienstplan unberührt)' },
                              { value: 'proportional', text: 'Schicht kürzen (anteilig im Dienstplan splitten)' },
                              { value: 'time_window', text: 'Exaktes Zeitfenster im Dienstplan markieren' },
                            ]"
                            outlined
                            dense
                            hide-details
                            class="rounded-lg"></v-select>
                        </v-col>
                        <v-col key="main-loc-col" cols="12" sm="4" v-if="sickPercentage < 100 && calculationMode === 'proportional'">
                          <v-select
                            label="Lage der Kürzung"
                            v-model="sickLocation"
                            :items="[
                              { value: 'start', text: 'Am Anfang der Schicht' },
                              { value: 'end', text: 'Am Ende der Schicht' },
                            ]"
                            outlined
                            dense
                            hide-details
                            class="rounded-lg"></v-select>
                        </v-col>
                        <v-col key="main-window-col" cols="12" v-if="sickPercentage < 100 && calculationMode === 'time_window'">
                          <v-text-field
                            v-model="sickTimeRange"
                            label="Exaktes Zeitfenster"
                            placeholder="z. B. 13:15 - 15:15"
                            hint="Mehrere Fenster mit Komma trennen."
                            persistent-hint
                            outlined
                            dense
                            class="rounded-lg"
                            @change="onSickTimeRangeTextChange"></v-text-field>
                        </v-col>

                        <!-- Erklärung der Verrechnungsart -->
                        <v-col cols="12" class="pt-1 pb-0" v-if="sickPercentage < 100">
                          <div class="grey--text text-caption" style="line-height: 1.3">
                            <span v-if="calculationMode === 'fixed'">
                              ℹ️ <strong>Schicht bleibt aktiv:</strong> Der Mitarbeiter bleibt voll im Dienstplan eingeteilt (keine Änderung der
                              Schicht).
                            </span>
                            <span v-if="calculationMode === 'proportional'">
                              ℹ️ <strong>Schicht kürzen:</strong> Die geplante Schicht wird im Dienstplan anteilig gekürzt (z. B. bei 50% werden 4h
                              gearbeitet und 4h krank gewertet).
                            </span>
                            <span v-if="calculationMode === 'time_window'">
                              ℹ️ <strong>Exaktes Zeitfenster:</strong> Nur die angegebenen Zeiten werden im Dienstplan als krank markiert.
                            </span>
                          </div>
                        </v-col>
                      </v-row>

                      <!-- Shift Info for current input if found -->
                      <v-expand-transition>
                        <div v-if="type && type !== 'holiday'" class="mt-3 pl-1 d-flex align-center flex-wrap">
                          <v-chip
                            v-if="currentDayShiftInfo"
                            color="rgba(0, 150, 63, 0.12)"
                            class="primary-green--text font-weight-medium px-3 py-1 text-subtitle-2"
                            style="height: auto; border: 1px solid rgba(0, 150, 63, 0.2)">
                            <v-icon small color="#00963f" class="mr-2">mdi-calendar-check</v-icon>
                            Gefunden: <strong class="ml-1">{{ currentDayShiftInfo.name }}</strong> &nbsp;({{ currentDayShiftInfo.times }})
                          </v-chip>
                          <v-chip
                            v-else-if="date && sicko"
                            color="rgba(255, 152, 0, 0.08)"
                            class="orange--text text--lighten-1 font-weight-medium px-3 py-1 text-subtitle-2"
                            style="height: auto; border: 1px solid rgba(255, 152, 0, 0.2)">
                            <v-icon small color="orange" class="mr-2">mdi-alert-circle-outline</v-icon>
                            Keine Schicht geplant (Stunden manuell)
                          </v-chip>
                        </div>
                        <div v-else-if="date && sicko && currentDayPercentage !== undefined" class="mt-3 pl-1 d-flex align-center flex-wrap">
                          <v-chip
                            color="rgba(0, 150, 63, 0.12)"
                            class="primary-green--text font-weight-medium px-3 py-1 text-subtitle-2"
                            style="height: auto; border: 1px solid rgba(0, 150, 63, 0.2)">
                            <v-icon small color="#00963f" class="mr-2">mdi-percent</v-icon>
                            Pensum: <strong class="ml-1">{{ currentDayPercentage }}%</strong> &nbsp;(Sollstunden: {{ currentDayTargetHours }} h)
                          </v-chip>
                        </div>
                      </v-expand-transition>
                    </v-card-text>
                  </v-card>

                  <div class="d-flex align-center mt-6">
                    <v-btn text @click="stepper--" class="mr-4 grey--text text--lighten-1">Zurück</v-btn>
                    <v-btn color="primary" class="px-6" @click="saveSickEntry">Speichern</v-btn>
                  </div>
                </v-form>
              </v-stepper-content>
            </v-stepper>
          </v-col>
        </v-row>
      </v-window-item>

      <!-- Tab 2: Einträge verwalten -->
      <v-window-item>
        <!-- Filter Card -->
        <v-card class="glass-card pa-4 mb-4 mx-4 rounded-lg" elevation="0" color="#1E1E1E">
          <v-row dense align="center">
            <!-- Status Filter Toggle -->
            <v-col cols="12" lg="4">
              <v-btn-toggle v-model="listStatusFilter" mandatory dense class="glass-toggle rounded-lg w-100">
                <v-btn value="all" class="toggle-btn flex-grow-1" active-class="active-blue"> Alle </v-btn>
                <v-btn value="pending" class="toggle-btn flex-grow-1" active-class="active-orange"> Ausstehend ({{ pendingCount }}) </v-btn>
                <v-btn value="accepted" class="toggle-btn flex-grow-1" active-class="active-green"> Genehmigt </v-btn>
                <v-btn value="rejected" class="toggle-btn flex-grow-1" active-class="active-red"> Abgelehnt </v-btn>
              </v-btn-toggle>
            </v-col>

            <!-- Employee Search Autocomplete -->
            <v-col cols="12" sm="6" lg="4">
              <v-autocomplete
                v-model="listEmployeeFilter"
                :items="availableEmployeesForFilter"
                item-text="fullName"
                item-value="_id"
                label="Mitarbeiter filtern"
                dense
                outlined
                hide-details
                clearable
                prepend-inner-icon="mdi-account-search"
                class="rounded-lg"></v-autocomplete>
            </v-col>

            <!-- Absence Reason Multi-Select -->
            <v-col cols="12" sm="6" lg="4" class="d-flex align-center">
              <v-select
                v-model="listTypeFilter"
                :items="typeItems"
                item-text="text"
                item-value="value"
                label="Abwesenheitstyp filtern"
                multiple
                chips
                small-chips
                deletable-chips
                dense
                outlined
                hide-details
                clearable
                prepend-inner-icon="mdi-filter-variant"
                class="rounded-lg flex-grow-1"></v-select>
            </v-col>
          </v-row>
        </v-card>

        <v-row v-if="listLoading">
          <v-col cols="12" class="text-center py-5">
            <v-progress-circular indeterminate color="primary"></v-progress-circular>
          </v-col>
        </v-row>
        <div v-else-if="existingEntries.length === 0" class="text-center py-10 grey--text">
          Keine Abwesenheitseinträge für das aktuelle Studio gefunden.
        </div>
        <div v-else-if="filteredAndSortedEntries.length === 0" class="text-center py-10 grey--text">
          Keine Abwesenheiten mit den gewählten Filtern gefunden.
        </div>
        <div v-else class="pa-4">
          <v-simple-table class="transparent-table rounded-lg overflow-hidden border-light">
            <template v-slot:default>
              <thead>
                <tr>
                  <th class="text-left grey--text">Mitarbeiter</th>
                  <th class="text-left grey--text">Grund</th>
                  <th class="text-left grey--text">Zeitraum & Details</th>
                  <th class="text-left grey--text">Status</th>
                  <th class="text-right grey--text">Aktionen</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="entry in filteredAndSortedEntries" :key="entry._id">
                  <td class="white--text font-weight-bold">
                    {{ entry.employee ? entry.employee.fullName || entry.employee.vorname + " " + entry.employee.nachname : "Unbekannt" }}
                  </td>
                  <td>
                    <v-chip small :color="getReasonColor(entry.absenceReason)" class="white--text font-weight-bold">
                      {{ mappedAbsenceTypes[entry.absenceReason] || entry.absenceReason || "Krank" }}
                    </v-chip>
                    <div class="text-caption grey--text mt-1" v-if="entry.note">
                      {{ entry.note }}
                    </div>
                  </td>
                  <td class="white--text">
                    <div class="font-weight-medium">
                      {{ formatDatesRange(entry.sickDays) }}
                    </div>
                    <div class="text-caption grey--text">
                      Gesamt: {{ calculateTotalHours(entry.sickDays) }} h ({{ entry.sickDays ? entry.sickDays.length : 0 }} Tage)
                      <span v-if="entry.sickDays && entry.sickDays.some((d) => getDisplaySickPercentage(d) < 100)" class="orange--text ml-1">
                        {{ getDisplayPercentageText(entry) }}
                      </span>
                    </div>
                  </td>
                  <td>
                    <v-chip small outlined :color="getStatusColor(entry.status)">
                      {{ getStatusText(entry.status) }}
                    </v-chip>
                  </td>
                  <td class="text-right">
                    <v-btn icon color="info" @click="downloadAttachment(entry)" v-if="entry.attachment" title="Arztzeugnis herunterladen">
                      <v-icon>mdi-file-eye-outline</v-icon>
                    </v-btn>
                    <template v-if="entry.isLocked && !userService.hasRight('stats_transactions')">
                      <v-btn icon disabled title="Dieser Monat ist bereits eingereicht oder genehmigt">
                        <v-icon color="grey">mdi-lock</v-icon>
                      </v-btn>
                    </template>
                    <template v-else>
                      <v-btn icon color="primary" @click="openEditDialog(entry)">
                        <v-icon>mdi-pencil</v-icon>
                      </v-btn>
                      <v-btn icon color="error" @click="deleteEntry(entry)">
                        <v-icon>mdi-delete</v-icon>
                      </v-btn>
                    </template>
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </div>
      </v-window-item>
    </v-window>

    <!-- Bearbeiten-Dialog -->
    <v-dialog v-model="editDialog" max-width="600" content-class="glass-card">
      <v-card color="#1E1E1E" class="rounded-lg" dark v-if="editingEntry">
        <v-card-title class="d-flex justify-space-between align-center">
          <span class="text-h5 font-weight-bold white--text">Abwesenheit bearbeiten</span>
          <v-btn icon @click="editDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-text>
          <div class="mb-4">
            <div class="text-caption grey--text mb-1">Mitarbeiter</div>
            <div class="text-subtitle-1 white--text font-weight-bold">
              {{
                editingEntry.employee ? editingEntry.employee.fullName || editingEntry.employee.vorname + " " + editingEntry.employee.nachname : ""
              }}
            </div>
          </div>

          <v-select label="Abwesenheitstyp" v-model="editingReason" :items="typeItems" outlined dense class="rounded-lg mb-2"></v-select>

          <v-text-field
            v-if="editingReason === 'other'"
            label="Grund für Abwesenheit (Pflichtfeld)"
            v-model="editingNote"
            outlined
            dense
            class="rounded-lg mb-2"></v-text-field>
          <v-text-field v-else label="Notiz (optional)" v-model="editingNote" outlined dense class="rounded-lg mb-2"></v-text-field>

          <!-- Status Info Box -->
          <v-alert
            v-if="editingEntry.status !== 'accepted'"
            color="rgba(255, 152, 0, 0.15)"
            class="rounded-lg text-left mb-4"
            style="border: 1px solid rgba(255, 152, 0, 0.3)">
            <div class="d-flex align-start">
              <v-icon color="orange" class="mr-3 mt-1">mdi-alert-decagram-outline</v-icon>
              <div class="caption white--text" style="line-height: 1.4">
                <strong>Status: Ausstehend</strong><br />
                Dieser Eintrag ist noch nicht freigegeben. Mit dem Klick auf <strong>"Freigeben & Speichern"</strong> wird der Eintrag genehmigt und
                in die Zeiterfassung übernommen.
              </div>
            </div>
          </v-alert>
          <v-alert v-else color="rgba(76, 175, 80, 0.15)" class="rounded-lg text-left mb-4" style="border: 1px solid rgba(76, 175, 80, 0.3)">
            <div class="d-flex align-start">
              <v-icon color="success" class="mr-3 mt-1">mdi-check-circle-outline</v-icon>
              <div class="caption white--text" style="line-height: 1.4">
                <strong>Status: Genehmigt</strong><br />
                Diese Abwesenheit ist bereits freigegeben und in die Zeiterfassung eingerechnet.
              </div>
            </div>
          </v-alert>

          <!-- AUB Attachment Preview -->
          <div v-if="editingEntry.attachment" class="mb-4">
            <div class="text-subtitle-2 white--text mb-2 font-weight-bold">Arztzeugnis (AUB):</div>
            <v-card color="rgba(255, 255, 255, 0.05)" class="rounded-lg pa-3 border-light d-flex align-center justify-space-between">
              <div class="d-flex align-center">
                <v-icon color="#E53935" class="mr-3" large>
                  {{ editingEntry.attachment.mimeType && editingEntry.attachment.mimeType.includes("pdf") ? "mdi-file-pdf-box" : "mdi-file-image" }}
                </v-icon>
                <div class="text-left">
                  <div class="white--text text-body-2 font-weight-bold text-truncate" style="max-width: 200px">
                    {{ editingEntry.attachment.originalName || "Arztzeugnis" }}
                  </div>
                  <div class="grey--text text-caption" v-if="editingEntry.attachment.size">
                    {{ (editingEntry.attachment.size / 1024 / 1024).toFixed(2) }} MB
                  </div>
                </div>
              </div>
              <v-btn small color="#00963f" class="black--text font-weight-bold" @click="downloadAttachment(editingEntry)">
                <v-icon left small>mdi-download</v-icon> Download
              </v-btn>
            </v-card>
          </div>

          <div class="text-subtitle-2 white--text mb-2 font-weight-bold">Tage und Stunden:</div>
          <div v-if="editingSickDays && editingSickDays.length > 0">
            <v-card
              v-for="(day, index) in editingSickDays"
              :key="index"
              color="rgba(255,255,255,0.03)"
              class="rounded-xl mb-3 border-light elevation-0"
              :style="'border-left: 4px solid ' + getBorderColor(editingReason) + ' !important;'">
              <v-card-text class="pa-3">
                <v-row dense class="align-center mx-0">
                  <v-col cols="12" sm="5">
                    <v-menu v-model="day.menu" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          v-model="day.date"
                          label="Datum"
                          readonly
                          outlined
                          dense
                          hide-details
                          prepend-inner-icon="mdi-calendar"
                          class="rounded-lg"
                          v-bind="attrs"
                          v-on="on"></v-text-field>
                      </template>
                      <v-date-picker
                        first-day-of-week="1"
                        locale="de-DE"
                        v-model="day.date"
                        @input="onEditingDayDateChanged(index, $event)"></v-date-picker>
                    </v-menu>
                  </v-col>
                  <v-col cols="9" sm="5">
                    <v-text-field
                      v-model="day.hours"
                      label="Stunden"
                      outlined
                      dense
                      hide-details
                      prepend-inner-icon="mdi-clock-outline"
                      class="rounded-lg"></v-text-field>
                  </v-col>
                  <v-col cols="3" sm="2" class="text-right">
                    <v-btn icon color="red" class="glass-btn" @click="removeEditingDay(index)">
                      <v-icon>mdi-trash-can-outline</v-icon>
                    </v-btn>
                  </v-col>
                </v-row>

                <v-row dense class="align-center mx-0 mt-2" v-if="editingReason && editingReason !== 'holiday'">
                  <v-col
                    :key="'edit-day-pct-' + index"
                    cols="12"
                    :sm="day.sickPercentage < 100 ? (day.calculationMode === 'proportional' ? 4 : 6) : 12">
                    <v-select
                      :label="getPercentageLabel(editingReason)"
                      v-model="day.sickPercentage"
                      :items="getSickPercentageOptions(day.sickPercentage)"
                      suffix="%"
                      outlined
                      dense
                      hide-details
                      class="rounded-lg"
                      @change="onEditingDayPercentageChange(index)"></v-select>
                  </v-col>
                  <v-col
                    :key="'edit-day-mode-' + index"
                    cols="12"
                    :sm="day.calculationMode === 'proportional' ? 4 : 6"
                    v-if="day.sickPercentage < 100">
                    <v-select
                      label="Dienstplan-Auswirkung"
                      v-model="day.calculationMode"
                      :items="[
                        { value: 'fixed', text: 'Schicht bleibt aktiv (Dienstplan unberührt)' },
                        { value: 'proportional', text: 'Schicht kürzen (anteilig im Dienstplan splitten)' },
                        { value: 'time_window', text: 'Exaktes Zeitfenster im Dienstplan markieren' },
                      ]"
                      outlined
                      dense
                      hide-details
                      class="rounded-lg"></v-select>
                  </v-col>
                  <v-col :key="'edit-day-loc-' + index" cols="12" sm="4" v-if="day.sickPercentage < 100 && day.calculationMode === 'proportional'">
                    <v-select
                      label="Lage der Kürzung"
                      v-model="day.sickLocation"
                      :items="[
                        { value: 'start', text: 'Am Anfang der Schicht' },
                        { value: 'end', text: 'Am Ende der Schicht' },
                      ]"
                      outlined
                      dense
                      hide-details
                      class="rounded-lg"></v-select>
                  </v-col>
                  <v-col :key="'edit-day-window-' + index" cols="12" v-if="day.sickPercentage < 100 && day.calculationMode === 'time_window'">
                    <v-text-field
                      v-model="day.sickTimeRange"
                      label="Exaktes Zeitfenster"
                      placeholder="z. B. 13:15 - 15:15"
                      hint="Mehrere Fenster mit Komma trennen."
                      persistent-hint
                      outlined
                      dense
                      class="rounded-lg"
                      @change="onEditingDayTimeRangeTextChange(index)"></v-text-field>
                  </v-col>

                  <!-- Erklärung der Verrechnungsart -->
                  <v-col :key="'edit-day-desc-' + index" cols="12" class="pt-1 pb-0" v-if="day.sickPercentage < 100">
                    <div class="grey--text text-caption" style="line-height: 1.3">
                      <span v-if="day.calculationMode === 'fixed'">
                        ℹ️ <strong>Schicht bleibt aktiv:</strong> Der Mitarbeiter bleibt voll im Dienstplan eingeteilt (keine Änderung der Schicht).
                      </span>
                      <span v-if="day.calculationMode === 'proportional'">
                        ℹ️ <strong>Schicht kürzen:</strong> Die geplante Schicht wird im Dienstplan anteilig gekürzt (z. B. bei 50% werden 4h
                        gearbeitet und 4h krank gewertet).
                      </span>
                      <span v-if="day.calculationMode === 'time_window'">
                        ℹ️ <strong>Exaktes Zeitfenster:</strong> Nur die angegebenen Zeiten werden im Dienstplan als krank markiert.
                      </span>
                    </div>
                  </v-col>
                </v-row>
                <!-- Shift Info Pill in Edit Dialog -->
                <v-expand-transition>
                  <div v-if="editingReason && editingReason !== 'holiday'" class="mt-2 pl-1 d-flex align-center flex-wrap" style="gap: 6px">
                    <v-chip
                      v-if="$moment(day.date).isoWeekday() >= 6"
                      color="rgba(255, 152, 0, 0.12)"
                      class="warning--text font-weight-black px-3 py-1 text-subtitle-2"
                      style="height: auto; border: 1px solid rgba(255, 152, 0, 0.3)">
                      <v-icon small color="orange" class="mr-2">mdi-calendar-weekend</v-icon>
                      Wochenende
                    </v-chip>
                    <v-chip
                      v-if="day.shiftInfo"
                      color="rgba(0, 150, 63, 0.12)"
                      class="primary-green--text font-weight-medium px-3 py-1 text-subtitle-2"
                      style="height: auto; border: 1px solid rgba(0, 150, 63, 0.2)">
                      <v-icon small color="#00963f" class="mr-2">mdi-calendar-check</v-icon>
                      Automatisch ermittelt: <strong class="ml-1">{{ day.shiftInfo.name }}</strong> &nbsp;({{ day.shiftInfo.times }})
                    </v-chip>
                    <v-chip
                      v-else-if="day.percentage !== undefined"
                      color="rgba(255, 152, 0, 0.08)"
                      class="orange--text text--lighten-1 font-weight-medium px-3 py-1 text-subtitle-2"
                      style="height: auto; border: 1px solid rgba(255, 152, 0, 0.2)">
                      <v-icon small color="orange" class="mr-2">mdi-alert-circle-outline</v-icon>
                      Keine Schicht geplant (Stunden manuell)
                    </v-chip>
                  </div>
                  <div v-else-if="day.percentage !== undefined" class="mt-2 pl-1 d-flex align-center flex-wrap" style="gap: 6px">
                    <v-chip
                      v-if="$moment(day.date).isoWeekday() >= 6"
                      color="rgba(255, 152, 0, 0.12)"
                      class="warning--text font-weight-black px-3 py-1 text-subtitle-2"
                      style="height: auto; border: 1px solid rgba(255, 152, 0, 0.3)">
                      <v-icon small color="orange" class="mr-2">mdi-calendar-weekend</v-icon>
                      Wochenende
                    </v-chip>
                    <v-chip
                      color="rgba(0, 150, 63, 0.12)"
                      class="primary-green--text font-weight-medium px-3 py-1 text-subtitle-2"
                      style="height: auto; border: 1px solid rgba(0, 150, 63, 0.2)">
                      <v-icon small color="#00963f" class="mr-2">mdi-percent</v-icon>
                      Pensum: <strong class="ml-1">{{ day.percentage }}%</strong> &nbsp;(Sollstunden: {{ day.targetHours }} h)
                    </v-chip>
                  </div>
                </v-expand-transition>
              </v-card-text>
            </v-card>
          </div>

          <v-btn text small color="#00963f" class="mt-2 pl-0 font-weight-bold" @click="addEditingDay">
            <v-icon left small>mdi-plus</v-icon> Tag hinzufügen
          </v-btn>
        </v-card-text>
        <v-card-actions class="pa-4 justify-end">
          <v-btn text color="grey" @click="editDialog = false">Abbrechen</v-btn>
          <v-btn color="#00963f" class="black--text font-weight-bold" @click="saveEdit" :loading="editLoading">
            {{ editingEntry.status !== "accepted" ? "Freigeben & Speichern" : "Speichern" }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import api from "@/api"
import userSearch from "@/components/userSearch.vue"
import axios from "axios"
import userService from "@/services/userService"

export default {
  name: "SickDaysView",
  components: { userSearch },
  props: {
    selectedStudioSingle: { required: false },
  },
  data() {
    return {
      userService,
      viewTab: 0,
      stepper: 1,
      date: null,
      type: null,
      requiresEmployeeApproval: false,
      note: "",
      datePickerMenu: false,
      sickDays: [],
      loading: false,
      sicko: null,
      hours: 0,
      sickPercentage: 100,
      sickTimeRange: "",
      sickTimeStart: "",
      sickTimeEnd: "",
      calculationMode: "fixed",
      sickLocation: "start",
      rules: {
        required: [(v) => !!v || "Dieses Feld ist ein Pflichtfeld"],
      },
      typeItems: [
        { value: "sick", text: "Krank" },
        { value: "school", text: "Schule" },
        { value: "military", text: "Militär" },
        { value: "pregnancy", text: "Schwangerschaft / Mutterschaft" },
        { value: "other", text: "Andere" },
      ],
      mappedAbsenceTypes: {
        sick: "Krank",
        Krankheit: "Krank",
        school: "Schule",
        military: "Militär",
        pregnancy: "Schwangerschaft / Mutterschaft",
        other: "Andere",
      },

      // Liste und Editieren
      listLoading: false,
      existingEntries: [],
      listStatusFilter: "all",
      listEmployeeFilter: null,
      listTypeFilter: [],
      editDialog: false,
      isOpeningEdit: false,
      editingEntry: null,
      editingReason: null,
      editingNote: "",
      editingSickDays: [],
      editLoading: false,
      currentDayShiftInfo: null,
      currentDayPercentage: undefined,
      currentDayTargetHours: undefined,
      suggestMonth: this.$moment().format("MM"),
      suggestYear: this.$moment().format("YYYY"),
      suggestLoading: false,
      monthItems: [
        { value: "01", text: "Januar" },
        { value: "02", text: "Februar" },
        { value: "03", text: "März" },
        { value: "04", text: "April" },
        { value: "05", text: "Mai" },
        { value: "06", text: "Juni" },
        { value: "07", text: "Juli" },
        { value: "08", text: "August" },
        { value: "09", text: "September" },
        { value: "10", text: "Oktober" },
        { value: "11", text: "November" },
        { value: "12", text: "Dezember" },
      ],
      yearItems: [this.$moment().subtract(1, "year").format("YYYY"), this.$moment().format("YYYY"), this.$moment().add(1, "year").format("YYYY")],
      lockedMonthsForSelectedEmployee: [],
      editingLockedMonths: [],
    }
  },
  computed: {
    availableEmployeesForFilter() {
      const emps = new Map()
      this.existingEntries.forEach((entry) => {
        if (entry.employee && entry.employee._id) {
          const fullName =
            entry.employee.fullName || `${entry.employee.vorname || ""} ${entry.employee.nachname || ""}`.trim() || "Unbekannter Mitarbeiter"
          emps.set(entry.employee._id, {
            _id: entry.employee._id,
            fullName,
          })
        }
      })
      return Array.from(emps.values()).sort((a, b) => a.fullName.localeCompare(b.fullName))
    },
    pendingCount() {
      return this.existingEntries.filter((e) => e.status === "pending_cl" || e.status === "pending_employee" || e.status === "open").length
    },
    filteredAndSortedEntries() {
      // 1. Filter
      let result = this.existingEntries.filter((entry) => {
        // Status Filter
        if (this.listStatusFilter === "pending") {
          if (entry.status !== "pending_cl" && entry.status !== "pending_employee" && entry.status !== "open") {
            return false
          }
        } else if (this.listStatusFilter === "accepted") {
          if (entry.status && entry.status !== "accepted") {
            return false
          }
        } else if (this.listStatusFilter === "rejected") {
          if (entry.status !== "rejected") {
            return false
          }
        }

        // Employee Filter
        if (this.listEmployeeFilter) {
          const empId = entry.employee?._id || entry.employee
          if (empId !== this.listEmployeeFilter) {
            return false
          }
        }

        // Type Filter
        if (this.listTypeFilter && this.listTypeFilter.length > 0) {
          if (!this.listTypeFilter.includes(entry.absenceReason)) {
            return false
          }
        }

        return true
      })

      // 2. Sort: pending first, then date descending
      return result.sort((a, b) => {
        const isPendingA = a.status === "pending_cl" || a.status === "pending_employee" || a.status === "open"
        const isPendingB = b.status === "pending_cl" || b.status === "pending_employee" || b.status === "open"

        if (isPendingA && !isPendingB) return -1
        if (!isPendingA && isPendingB) return 1

        const getMinDate = (entry) => {
          if (!entry.sickDays || !entry.sickDays.length) return ""
          const dates = entry.sickDays.map((sd) => sd.date)
          return dates.reduce((min, d) => (d < min ? d : min), dates[0])
        }

        const dateA = getMinDate(a)
        const dateB = getMinDate(b)
        return dateB.localeCompare(dateA)
      })
    },
  },
  methods: {
    getBorderColor(reason) {
      if (reason === "sick") return "#E53935" // red
      if (reason === "school") return "#1E88E5" // blue
      if (reason === "military") return "#8E24AA" // purple
      if (reason === "pregnancy") return "#00897B" // teal
      return "#00963f" // default green
    },
    weiterSchritt2() {
      if (this.type === "other" && !this.note) {
        this.$toast.error("Bitte zwingend einen Grund eintragen.")
        return
      }
      this.stepper++
    },
    async fetchSchoolDaysSuggestions() {
      if (!this.sicko) {
        this.$toast.error("Bitte wähle zuerst einen Mitarbeiter in Schritt 1 aus.")
        return
      }
      this.suggestLoading = true
      try {
        const response = await axios.get(`/api/timetracker/school-days/suggest/${this.sicko}/${this.suggestYear}/${this.suggestMonth}`, {
          headers: { Authorization: localStorage.getItem("token") },
        })
        if (response.data && response.data.length > 0) {
          const filteredSuggestions = response.data.filter((sd) => {
            const m = this.$moment(sd.date).format("YYYY-MM")
            return userService.hasRight("stats_transactions") || !this.lockedMonthsForSelectedEmployee.includes(m)
          })
          if (filteredSuggestions.length === 0) {
            this.$toast.info("Keine Berufsschultage für diesen Zeitraum gefunden (oder bereits eingereicht/genehmigt).")
            return
          }
          this.sickDays = filteredSuggestions.map((sd) => ({
            date: sd.date,
            hours: sd.hours,
            datePickerMenu: false,
            shiftInfo: null,
            percentage: undefined,
            targetHours: undefined,
          }))

          this.$toast.success(
            `${filteredSuggestions.length} Berufsschultage für ${this.monthItems.find((m) => m.value === this.suggestMonth).text} vorgeschlagen.`,
          )
          this.stepper = 3
        } else {
          this.$toast.info("Keine Berufsschultage für diesen Zeitraum gefunden (oder bereits erfasst).")
        }
      } catch (err) {
        console.error("Error fetching school days suggestions:", err)
        this.$toast.error("Fehler beim Abrufen der Vorschläge")
      } finally {
        this.suggestLoading = false
      }
    },
    addSickDay() {
      if (this.$refs.form.validate()) {
        const monthStr = this.$moment(this.date).format("YYYY-MM")
        if (!userService.hasRight("stats_transactions") && this.lockedMonthsForSelectedEmployee.includes(monthStr)) {
          this.$toast.error("Dieser Monat ist bereits eingereicht oder genehmigt und kann nicht bearbeitet werden.")
          return
        }

        let nextSickPercentage = this.type !== "holiday" ? this.sickPercentage || 100 : 100
        let nextSickTimeRange = this.type !== "holiday" ? this.sickTimeRange || "" : ""
        let nextCalculationMode = this.type !== "holiday" ? this.calculationMode || "fixed" : "fixed"
        let nextSickLocation = this.type !== "holiday" ? this.sickLocation || "start" : "start"
        if (this.sickDays.length > 0) {
          const lastDay = this.sickDays[this.sickDays.length - 1]
          nextSickPercentage = lastDay.sickPercentage !== undefined ? lastDay.sickPercentage : 100
          nextSickTimeRange = lastDay.sickTimeRange || ""
          nextCalculationMode = lastDay.calculationMode || "fixed"
          nextSickLocation = lastDay.sickLocation || "start"
        }

        const parsedTime = this.parseTimeRange(nextSickTimeRange)
        this.sickDays.push({
          date: this.date,
          hours: this.hours,
          datePickerMenu: false,
          shiftInfo: this.currentDayShiftInfo ? { ...this.currentDayShiftInfo } : null,
          percentage: this.currentDayPercentage,
          targetHours: this.currentDayTargetHours,
          sickPercentage: nextSickPercentage,
          sickTimeRange: nextSickTimeRange,
          sickTimeStart: parsedTime.start,
          sickTimeEnd: parsedTime.end,
          calculationMode: nextCalculationMode,
          sickLocation: nextSickLocation,
          shiftInfoTotalHours: this.currentDayShiftTotalHours || 0,
        })
        this.date = this.$moment(this.date).add(1, "days").format("YYYY-MM-DD")
        this.$refs.form.resetValidation()
      }
    },
    updateSicko(sickoId) {
      this.sicko = sickoId
    },
    async fetchLockedMonthsForEmployee(employeeId) {
      if (!employeeId) {
        this.lockedMonthsForSelectedEmployee = []
        return
      }
      try {
        const response = await axios.get(`/api/timetracker/management/employee-locked-months/${employeeId}`, {
          headers: { Authorization: localStorage.getItem("token") },
        })
        this.lockedMonthsForSelectedEmployee = response.data.lockedMonths || []
      } catch (err) {
        console.error("Error fetching locked months for employee:", err)
        this.lockedMonthsForSelectedEmployee = []
      }
    },
    reset() {
      this.sicko = null
      this.stepper = 1
      this.sickDays = []
      this.note = ""
      this.type = null
      this.requiresEmployeeApproval = false
      this.lockedMonthsForSelectedEmployee = []
    },
    async saveSickEntry() {
      if (!this.sickDays.length) {
        this.$toast.error("Bitte mindestens einen Tag eintragen (Grünes Häckchen gedrückt? ;)")
        return
      }
      if (!this.sicko) {
        this.$toast.error("Bitte eine Person auswählen")
        return
      }
      if (!this.type) {
        this.$toast.error("Bitte einen Abwesenheitstyp auswählen")
        return
      }
      if (this.type === "other" && !this.note) {
        this.$toast.error("Bitte einen Grund für die Abwesenheit angeben")
        this.stepper = 2
        return
      }
      const hasLockedDay =
        !userService.hasRight("stats_transactions") &&
        this.sickDays.some((sd) => {
          const m = this.$moment(sd.date).format("YYYY-MM")
          return this.lockedMonthsForSelectedEmployee.includes(m)
        })
      if (hasLockedDay) {
        this.$toast.error("Einige der ausgewählten Tage fallen in einen eingereichten oder genehmigten Monat.")
        return
      }
      if (this.sickDays.some((sd) => sd.calculationMode === "time_window" && !this.calculateHoursFromRanges(sd.sickTimeRange))) {
        this.$toast.error("Bitte für das exakte Zeitfenster mindestens eine gültige Zeitspanne angeben.")
        return
      }
      const payloadDays = this.sickDays.map((sd) => ({
        date: sd.date,
        hours: parseFloat(String(sd.hours).replace(",", ".")) || 0,
        time: "-",
        sickPercentage: sd.sickPercentage,
        sickTimeRange: sd.sickTimeRange,
        calculationMode: sd.calculationMode || "proportional",
        sickLocation: sd.sickLocation || "start",
        shiftInfoTotalHours: sd.shiftInfoTotalHours,
      }))
      this.loading = true
      try {
        let status = await api.createSickEntryManagement({
          employee: this.sicko,
          sickDays: payloadDays,
          absenceReason: this.type,
          note: this.note,
          status: this.requiresEmployeeApproval ? "pending_employee" : "accepted",
        })
        if (!status._id) {
          this.$toast.error("Fehler beim Speichern des Abwesenheitseintrags")
          this.loading = false
          return
        }
        this.$toast.success("Abwesenheitseintrag gespeichert")
        this.$emit("fetchEntries")
        this.reset()
        if (this.viewTab === 1) {
          await this.fetchSickEntries()
        }
      } catch (err) {
        console.error(err)
        this.$toast.error("Fehler beim Speichern")
      } finally {
        this.loading = false
      }
    },

    // Verwaltungsmethoden
    async fetchSickEntries() {
      if (!this.selectedStudioSingle || this.selectedStudioSingle === "undefined" || this.selectedStudioSingle === "null") return
      this.listLoading = true
      try {
        this.existingEntries = await api.fetchSickEntriesManagement(this.selectedStudioSingle)
        this.$emit("updateCount")
      } catch (err) {
        console.error("Error fetching sick entries:", err)
        this.$toast.error("Fehler beim Laden der Abwesenheiten")
      } finally {
        this.listLoading = false
      }
    },
    getStatusColor(status) {
      if (status === "accepted" || !status) return "green"
      if (status === "rejected") return "red"
      if (status === "pending_employee") return "orange"
      if (status === "pending_cl" || status === "open") return "amber"
      return "grey"
    },
    getStatusText(status) {
      if (!status || status === "accepted") return "Genehmigt"
      if (status === "rejected") return "Abgelehnt"
      if (status === "pending_employee") return "Ausstehend (Mitarbeiter)"
      if (status === "pending_cl") return "Ausstehend (Freigabe)"
      if (status === "open") return "Offen"
      return status
    },
    getReasonColor(reason) {
      if (reason === "sick") return "red darken-2"
      if (reason === "school") return "blue darken-2"
      if (reason === "military") return "purple darken-2"
      if (reason === "pregnancy") return "teal darken-2"
      return "grey darken-1"
    },
    getPercentageLabel(reason) {
      const labels = {
        sick: "Krankheit %",
        pregnancy: "Schwangerschaft / Mutterschaft %",
        school: "Schule %",
        military: "Militär %",
      }
      return labels[reason] || "Abwesenheit %"
    },
    getTimeRangeLabel(reason) {
      if (reason === "sick") return "Krank-Zeitfenster (z.B. 13:00 - 17:00)"
      return "Abwesenheits-Zeitfenster (z.B. 13:00 - 17:00)"
    },
    getDisplayPercentageText(entry) {
      const day = entry.sickDays.find((d) => this.getDisplaySickPercentage(d) < 100)
      if (!day) return ""
      const pct = this.getDisplaySickPercentage(day)
      const label = entry.absenceReason === "sick" ? "krank" : "abwesend"
      return `(${pct}% ${label})`
    },
    formatDatesRange(sickDays) {
      if (!sickDays || !sickDays.length) return ""
      const sorted = [...sickDays].sort((a, b) => a.date.localeCompare(b.date))
      if (sorted.length === 1) return this.$moment(sorted[0].date).format("DD.MM.YYYY")
      return `${this.$moment(sorted[0].date).format("DD.MM.YYYY")} - ${this.$moment(sorted[sorted.length - 1].date).format("DD.MM.YYYY")}`
    },
    calculateTotalHours(sickDays) {
      if (!sickDays || !sickDays.length) return 0
      const total = sickDays.reduce((sum, d) => sum + (parseFloat(String(d.hours).replace(",", ".")) || 0), 0)
      return Math.round(total * 100) / 100
    },
    async deleteEntry(entry) {
      const empName = entry.employee ? entry.employee.fullName || entry.employee.vorname + " " + entry.employee.nachname : "diesen Mitarbeiter"
      if (!confirm(`Abwesenheit für ${empName} wirklich löschen?`)) return
      this.listLoading = true
      try {
        await axios.delete(`/api/timetracker/management/sick/${entry._id}`, {
          headers: { Authorization: localStorage.getItem("token") },
        })
        this.$toast.success("Abwesenheit gelöscht")
        await this.fetchSickEntries()
        this.$emit("fetchEntries")
      } catch (err) {
        console.error(err)
        this.$toast.error("Fehler beim Löschen der Abwesenheit")
      } finally {
        this.listLoading = false
      }
    },
    openEditDialog(entry) {
      this.isOpeningEdit = true
      this.editingEntry = entry
      this.editingLockedMonths = entry.lockedMonths || []
      let reason = entry.absenceReason
      if (reason === "Krankheit") reason = "sick"
      this.editingReason = reason
      this.editingNote = entry.note || ""

      const empId = entry.employee?._id || entry.employee
      const mappedDays = entry.sickDays.map((sd) => {
        let sp = sd.sickPercentage
        if (sp === undefined) {
          sp = sd.percentage !== undefined ? sd.percentage : 100
        }
        const totalHours = sd.shiftInfoTotalHours || (sd.hours && sp ? Math.round((sd.hours / (sp / 100)) * 100) / 100 : 0)
        const range = sd.sickTimeRange || (sd.time && sd.time !== "-" ? sd.time : "")
        const parsed = this.parseTimeRange(range)
        return {
          date: sd.date,
          hours: sd.hours,
          sickPercentage: sp,
          sickTimeRange: range,
          sickTimeStart: parsed.start,
          sickTimeEnd: parsed.end,
          calculationMode: sd.calculationMode || (range && sp < 100 ? "time_window" : "fixed"),
          sickLocation: sd.sickLocation || "start",
          shiftInfoTotalHours: totalHours,
          menu: false,
          shiftInfo: null,
          percentage: sd.percentage,
          targetHours: sd.targetHours,
        }
      })
      this.editingSickDays = mappedDays
      this.editDialog = true

      if (empId) {
        mappedDays.forEach(async (day, index) => {
          await this.checkShiftHours(empId, day.date, (resData) => {
            if (this.editingSickDays[index]) {
              this.editingSickDays[index].percentage = resData.percentage
              this.editingSickDays[index].targetHours = resData.targetHours
              if (resData.hours && !this.editingSickDays[index].shiftInfoTotalHours) {
                this.editingSickDays[index].shiftInfoTotalHours = resData.hours
              }

              if (this.editingReason === "sick") {
                if (resData.hasShift) {
                  this.editingSickDays[index].shiftInfo = {
                    name: resData.shiftName,
                    times: resData.times,
                  }
                } else {
                  this.editingSickDays[index].shiftInfo = null
                }
              } else {
                this.editingSickDays[index].shiftInfo = null
              }
            }
          })
        })
      }

      this.$nextTick(() => {
        this.isOpeningEdit = false
      })
    },
    async addEditingDay() {
      let newDate = this.$moment().format("YYYY-MM-DD")
      if (this.editingSickDays.length > 0) {
        newDate = this.$moment(this.editingSickDays[this.editingSickDays.length - 1].date)
          .add(1, "day")
          .format("YYYY-MM-DD")
      }
      const monthStr = this.$moment(newDate).format("YYYY-MM")
      if (!userService.hasRight("stats_transactions") && this.editingLockedMonths.includes(monthStr)) {
        this.$toast.error("Dieser Monat ist bereits eingereicht oder genehmigt.")
        return
      }
      let prevSickPercentage = this.editingReason !== "holiday" ? 100 : 100
      let prevSickTimeRange = ""
      let prevCalculationMode = "fixed"
      let prevSickLocation = "start"
      if (this.editingSickDays.length > 0) {
        const lastDay = this.editingSickDays[this.editingSickDays.length - 1]
        prevSickPercentage = lastDay.sickPercentage !== undefined ? lastDay.sickPercentage : 100
        prevSickTimeRange = lastDay.sickTimeRange || ""
        prevCalculationMode = lastDay.calculationMode || "fixed"
        prevSickLocation = lastDay.sickLocation || "start"
      }

      this.editingSickDays.push({
        date: newDate,
        hours: 8.4,
        menu: false,
        shiftInfo: null,
        percentage: undefined,
        targetHours: undefined,
        sickPercentage: prevSickPercentage,
        sickTimeRange: prevSickTimeRange,
        calculationMode: prevCalculationMode,
        sickLocation: prevSickLocation,
      })
      const empId = this.editingEntry?.employee?._id || this.editingEntry?.employee
      if (empId) {
        const idx = this.editingSickDays.length - 1
        await this.checkShiftHours(empId, newDate, (resData) => {
          if (this.editingSickDays[idx]) {
            this.editingSickDays[idx].percentage = resData.percentage
            this.editingSickDays[idx].targetHours = resData.targetHours

            if (this.editingReason && this.editingReason !== "holiday") {
              const isWeekend = this.$moment(newDate).isoWeekday() >= 6
              let baseHours = 0
              if (resData.hasShift) {
                baseHours = resData.hours || 0
              } else if (!isWeekend) {
                baseHours = resData.targetHours || 0
              }
              this.editingSickDays[idx].hours = Math.round(baseHours * (prevSickPercentage / 100) * 100) / 100
              this.editingSickDays[idx].shiftInfoTotalHours = resData.hours || 0
              if (resData.hasShift) {
                this.editingSickDays[idx].shiftInfo = {
                  name: resData.shiftName,
                  times: resData.times,
                }
              } else {
                this.editingSickDays[idx].shiftInfo = null
              }
            } else {
              this.editingSickDays[idx].hours = resData.targetHours
              this.editingSickDays[idx].shiftInfo = null
            }
          }
        })
      }
    },
    removeEditingDay(index) {
      this.editingSickDays.splice(index, 1)
    },
    async saveEdit() {
      if (!this.editingReason) {
        this.$toast.error("Bitte einen Abwesenheitstyp auswählen")
        return
      }
      if (this.editingReason === "other" && !this.editingNote) {
        this.$toast.error("Bitte einen Grund für die Abwesenheit angeben")
        return
      }
      if (this.editingSickDays.length === 0) {
        this.$toast.error("Bitte mindestens einen Tag angeben")
        return
      }
      const hasLockedDay =
        !userService.hasRight("stats_transactions") &&
        this.editingSickDays.some((d) => {
          const m = this.$moment(d.date).format("YYYY-MM")
          return this.editingLockedMonths.includes(m)
        })
      if (hasLockedDay) {
        this.$toast.error("Einige der ausgewählten Tage fallen in einen eingereichten oder genehmigten Monat.")
        return
      }
      if (this.editingSickDays.some((day) => day.calculationMode === "time_window" && !this.calculateHoursFromRanges(day.sickTimeRange))) {
        this.$toast.error("Bitte für das exakte Zeitfenster mindestens eine gültige Zeitspanne angeben.")
        return
      }
      this.editLoading = true
      try {
        const formattedDays = this.editingSickDays.map((d) => ({
          date: d.date,
          hours: parseFloat(String(d.hours).replace(",", ".")) || 0,
          time: "-",
          sickPercentage: d.sickPercentage,
          sickTimeRange: d.sickTimeRange,
          calculationMode: d.calculationMode || "fixed",
          sickLocation: d.sickLocation || "start",
          shiftInfoTotalHours: d.shiftInfoTotalHours,
        }))
        await api.updateSickEntryManagement(this.editingEntry._id, {
          absenceReason: this.editingReason,
          note: this.editingNote,
          sickDays: formattedDays,
        })
        this.$toast.success("Abwesenheit aktualisiert")
        this.editDialog = false
        await this.fetchSickEntries()
        this.$emit("fetchEntries")
      } catch (err) {
        console.error(err)
        this.$toast.error("Fehler beim Aktualisieren der Abwesenheit")
      } finally {
        this.editLoading = false
      }
    },
    async onSickDayDateChanged(index, selectedDate) {
      const monthStr = this.$moment(selectedDate).format("YYYY-MM")
      if (!userService.hasRight("stats_transactions") && this.lockedMonthsForSelectedEmployee.includes(monthStr)) {
        this.$toast.error("Dieser Monat ist bereits eingereicht oder genehmigt und kann nicht bearbeitet werden.")
        this.sickDays[index].date = ""
        this.sickDays[index].datePickerMenu = false
        return
      }
      this.sickDays[index].date = selectedDate
      this.sickDays[index].datePickerMenu = false
      if (this.sicko) {
        await this.checkShiftHours(this.sicko, selectedDate, (resData) => {
          if (this.sickDays[index]) {
            this.sickDays[index].percentage = resData.percentage
            this.sickDays[index].targetHours = resData.targetHours
            this.sickDays[index].sickPercentage = 100
            this.sickDays[index].sickTimeRange = resData.hasShift ? resData.times || "" : ""
            this.sickDays[index].shiftInfoTotalHours = resData.hours || 0

            if (this.type === "sick") {
              if (resData.hasShift) {
                this.sickDays[index].hours = resData.cappedHours
                this.sickDays[index].shiftInfo = {
                  name: resData.shiftName,
                  times: resData.times,
                }
              } else {
                this.sickDays[index].hours = 0
                this.sickDays[index].shiftInfo = null
              }
            } else {
              this.sickDays[index].hours = resData.targetHours
              this.sickDays[index].shiftInfo = null
            }
          }
        })
      }
    },
    async onEditingDayDateChanged(index, selectedDate) {
      const monthStr = this.$moment(selectedDate).format("YYYY-MM")
      if (!userService.hasRight("stats_transactions") && this.editingLockedMonths.includes(monthStr)) {
        this.$toast.error("Dieser Monat ist bereits eingereicht oder genehmigt und kann nicht bearbeitet werden.")
        this.editingSickDays[index].date = ""
        this.editingSickDays[index].menu = false
        return
      }
      this.editingSickDays[index].date = selectedDate
      this.editingSickDays[index].menu = false
      const empId = this.editingEntry?.employee?._id || this.editingEntry?.employee
      if (empId) {
        await this.checkShiftHours(empId, selectedDate, (resData) => {
          if (this.editingSickDays[index]) {
            this.editingSickDays[index].percentage = resData.percentage
            this.editingSickDays[index].targetHours = resData.targetHours
            this.editingSickDays[index].sickPercentage = 100
            this.editingSickDays[index].calculationMode = "fixed"
            this.editingSickDays[index].sickLocation = "start"
            this.editingSickDays[index].sickTimeRange = resData.hasShift ? resData.times || "" : ""
            this.editingSickDays[index].shiftInfoTotalHours = resData.hours || 0

            if (this.editingReason === "sick") {
              if (resData.hasShift) {
                this.editingSickDays[index].hours = resData.cappedHours
                this.editingSickDays[index].shiftInfo = {
                  name: resData.shiftName,
                  times: resData.times,
                }
              } else {
                this.editingSickDays[index].hours = 0
                this.editingSickDays[index].shiftInfo = null
              }
            } else {
              this.editingSickDays[index].hours = resData.targetHours
              this.editingSickDays[index].shiftInfo = null
            }
          }
        })
      }
    },
    async downloadAttachment(entry) {
      if (!entry.attachment) return
      try {
        const response = await axios.get(`/api/timetracker/sick/${entry._id}/attachment`, {
          headers: { Authorization: localStorage.getItem("token") },
          responseType: "blob",
        })
        const blob = new Blob([response.data], { type: entry.attachment.mimeType })
        const downloadUrl = window.URL.createObjectURL(blob)
        const link = document.createElement("a")
        link.href = downloadUrl
        link.setAttribute("download", entry.attachment.originalName || "arztzeugnis")
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(downloadUrl)
      } catch (err) {
        console.error("Error downloading attachment:", err)
        this.$toast.error("Fehler beim Herunterladen des Arztzeugnisses")
      }
    },
    async checkShiftHours(employeeId, dateStr, callback) {
      try {
        const res = await axios.get(`/api/shift-management/employee-shift-hours/${employeeId}/${dateStr}`, {
          headers: { Authorization: localStorage.getItem("token") },
        })
        callback(res.data)
      } catch (err) {
        console.error("Error checking shift hours:", err)
      }
    },
    async loadCurrentDayHours() {
      if (this.date && this.sicko) {
        this.hours = 0
        this.currentDayShiftInfo = null
        this.currentDayPercentage = undefined
        this.currentDayTargetHours = undefined

        let carryOverPercentage = 100
        let carryOverTimeRange = ""
        let carryOverCalculationMode = "fixed"
        let carryOverLocation = "start"
        if (this.sickDays.length > 0) {
          const lastDay = this.sickDays[this.sickDays.length - 1]
          carryOverPercentage = lastDay.sickPercentage !== undefined ? lastDay.sickPercentage : 100
          carryOverTimeRange = lastDay.sickTimeRange || ""
          carryOverCalculationMode = lastDay.calculationMode || "fixed"
          carryOverLocation = lastDay.sickLocation || "start"
        }

        this.sickPercentage = carryOverPercentage
        this.sickTimeRange = carryOverTimeRange
        const initialParsed = this.parseTimeRange(carryOverTimeRange)
        this.sickTimeStart = initialParsed.start
        this.sickTimeEnd = initialParsed.end
        this.calculationMode = carryOverCalculationMode
        this.sickLocation = carryOverLocation

        await this.checkShiftHours(this.sicko, this.date, (resData) => {
          this.currentDayPercentage = resData.percentage
          this.currentDayTargetHours = resData.targetHours
          this.currentDayShiftTotalHours = resData.hours || 0

          if (this.type && this.type !== "holiday") {
            const isWeekend = this.$moment(this.date).isoWeekday() >= 6
            let baseHours = 0
            if (resData.hasShift) {
              baseHours = resData.hours || 0
            } else if (!isWeekend) {
              baseHours = resData.targetHours || 0
            }
            this.hours = Math.round(baseHours * (carryOverPercentage / 100) * 100) / 100

            // If they had a shift, default the time range to the shift's times if they don't have a carryOver one
            if (resData.hasShift) {
              this.sickTimeRange = carryOverTimeRange || resData.times || ""
              const parsed = this.parseTimeRange(this.sickTimeRange)
              this.sickTimeStart = parsed.start
              this.sickTimeEnd = parsed.end
              this.currentDayShiftInfo = {
                name: resData.shiftName,
                times: resData.times,
              }
            } else {
              const parsed = this.parseTimeRange(this.sickTimeRange)
              this.sickTimeStart = parsed.start
              this.sickTimeEnd = parsed.end
              this.currentDayShiftInfo = null
              this.$toast.info(`Keine Schicht für den ${this.$moment(this.date).format("DD.MM.YYYY")} geplant. Bitte Stunden ggf. manuell anpassen.`)
            }
          } else {
            this.hours = resData.targetHours
            this.currentDayShiftInfo = null
          }
        })
      }
    },
    onPercentageChange(pct) {
      if (pct === 100) {
        this.calculationMode = "fixed"
      }
      const baseHours = this.currentDayShiftInfo ? this.currentDayShiftTotalHours : this.currentDayTargetHours
      this.hours = Math.round(baseHours * (pct / 100) * 100) / 100
    },
    onTimeChange() {
      if (this.sickTimeStart && this.sickTimeEnd) {
        this.sickTimeRange = `${this.sickTimeStart} - ${this.sickTimeEnd}`
        const duration = this.calculateHoursFromRange(this.sickTimeRange)
        if (duration !== null) {
          this.hours = duration
        }
      } else {
        this.sickTimeRange = ""
      }
    },
    onAddedDayTimeChange(index) {
      const day = this.sickDays[index]
      if (day.sickTimeStart && day.sickTimeEnd) {
        day.sickTimeRange = `${day.sickTimeStart} - ${day.sickTimeEnd}`
        const duration = this.calculateHoursFromRange(day.sickTimeRange)
        if (duration !== null) {
          day.hours = duration
        }
      } else {
        day.sickTimeRange = ""
      }
    },
    onEditingDayTimeChange(index) {
      const day = this.editingSickDays[index]
      if (day.sickTimeStart && day.sickTimeEnd) {
        day.sickTimeRange = `${day.sickTimeStart} - ${day.sickTimeEnd}`
        const duration = this.calculateHoursFromRange(day.sickTimeRange)
        if (duration !== null) {
          day.hours = duration
        }
      } else {
        day.sickTimeRange = ""
      }
    },
    onSickTimeRangeTextChange() {
      const duration = this.calculateHoursFromRanges(this.sickTimeRange)
      if (duration === null) return
      this.hours = duration
      if (this.currentDayShiftTotalHours > 0) {
        const maximum = duration < this.currentDayShiftTotalHours ? 99 : 100
        this.sickPercentage = Math.max(1, Math.min(maximum, Math.round((duration / this.currentDayShiftTotalHours) * 100)))
      }
    },
    onAddedDayTimeRangeTextChange(index) {
      const day = this.sickDays[index]
      const duration = this.calculateHoursFromRanges(day.sickTimeRange)
      if (duration === null) return
      day.hours = duration
      if (day.shiftInfoTotalHours > 0) {
        const maximum = duration < day.shiftInfoTotalHours ? 99 : 100
        day.sickPercentage = Math.max(1, Math.min(maximum, Math.round((duration / day.shiftInfoTotalHours) * 100)))
      }
    },
    onEditingDayTimeRangeTextChange(index) {
      const day = this.editingSickDays[index]
      const duration = this.calculateHoursFromRanges(day.sickTimeRange)
      if (duration === null) return
      day.hours = duration
      if (day.shiftInfoTotalHours > 0) {
        const maximum = duration < day.shiftInfoTotalHours ? 99 : 100
        day.sickPercentage = Math.max(1, Math.min(maximum, Math.round((duration / day.shiftInfoTotalHours) * 100)))
      }
    },
    parseTimeRange(rangeStr) {
      let start = ""
      let end = ""
      if (rangeStr && rangeStr.includes("-")) {
        const parts = rangeStr.split("-")
        if (parts.length === 2) {
          start = parts[0].trim()
          end = parts[1].trim()
        }
      }
      return { start, end }
    },
    calculateHoursFromRange(rangeStr) {
      return this.calculateHoursFromRanges(rangeStr)
    },
    calculateHoursFromRanges(rangeStr) {
      if (!rangeStr) return null
      const ranges = []
      const matcher = /(\d{1,2}:\d{2})\s*-\s*(\d{1,2}:\d{2})/g
      let match
      while ((match = matcher.exec(String(rangeStr))) !== null) {
        const toMinutes = (value) => {
          const parts = value.split(":").map(Number)
          if (parts.length !== 2 || parts[0] < 0 || parts[0] > 23 || parts[1] < 0 || parts[1] > 59) return null
          return parts[0] * 60 + parts[1]
        }
        const start = toMinutes(match[1])
        let end = toMinutes(match[2])
        if (start === null || end === null) continue
        if (end <= start) end += 24 * 60
        ranges.push({ start, end })
      }
      if (!ranges.length) return null

      ranges.sort((left, right) => left.start - right.start || left.end - right.end)
      const merged = []
      ranges.forEach((range) => {
        const last = merged[merged.length - 1]
        if (last && range.start <= last.end) {
          last.end = Math.max(last.end, range.end)
        } else {
          merged.push({ ...range })
        }
      })
      const minutes = merged.reduce((sum, range) => sum + range.end - range.start, 0)
      return Math.round((minutes / 60) * 100) / 100
    },
    onAddedDayPercentageChange(index) {
      const day = this.sickDays[index]
      if (day.sickPercentage === 100) {
        day.calculationMode = "fixed"
      }
      const baseHours = day.shiftInfo ? day.shiftInfoTotalHours || day.hours : day.targetHours
      const pct = day.sickPercentage || 100
      day.hours = Math.round(baseHours * (pct / 100) * 100) / 100
    },
    onEditingDayPercentageChange(index) {
      const day = this.editingSickDays[index]
      if (day.sickPercentage === 100) {
        day.calculationMode = "fixed"
      }
      const baseHours = day.shiftInfo ? day.shiftInfoTotalHours || day.hours : day.targetHours
      const pct = day.sickPercentage || 100
      day.hours = Math.round(baseHours * (pct / 100) * 100) / 100
    },
    getDisplaySickPercentage(day) {
      if (!day) return 100
      if (day.sickPercentage !== undefined) {
        return day.sickPercentage
      }
      if (day.percentage !== undefined && day.percentage < 100) {
        return day.percentage
      }
      return 100
    },
    getSickPercentageOptions(value) {
      return [...new Set([100, 90, 80, 75, 70, 60, 50, 40, 30, 25, 20, 10, Number(value)])]
        .filter((percentage) => Number.isFinite(percentage) && percentage > 0 && percentage <= 100)
        .sort((left, right) => right - left)
    },
  },
  watch: {
    viewTab(newVal) {
      if (newVal === 1) {
        this.fetchSickEntries()
      }
    },
    selectedStudioSingle: {
      handler() {
        if (this.viewTab === 1) {
          this.fetchSickEntries()
        }
      },
      immediate: true,
    },
    async date(newVal) {
      if (newVal) {
        await this.loadCurrentDayHours()
      }
    },
    async sicko(newVal) {
      if (newVal) {
        await this.fetchLockedMonthsForEmployee(newVal)
        await this.loadCurrentDayHours()
      } else {
        this.lockedMonthsForSelectedEmployee = []
      }
    },
    async type(newVal) {
      if (newVal) {
        await this.loadCurrentDayHours()
      }
    },
    async editingReason(newVal, oldVal) {
      if (this.isOpeningEdit || !oldVal) return
      const empId = this.editingEntry?.employee?._id || this.editingEntry?.employee
      if (empId && this.editingSickDays.length > 0) {
        this.editingSickDays.forEach(async (day, index) => {
          await this.checkShiftHours(empId, day.date, (resData) => {
            if (this.editingSickDays[index]) {
              this.editingSickDays[index].percentage = resData.percentage
              this.editingSickDays[index].targetHours = resData.targetHours

              if (newVal && newVal !== "holiday") {
                const pct = this.editingSickDays[index].sickPercentage !== undefined ? this.editingSickDays[index].sickPercentage : 100
                const isWeekend = this.$moment(day.date).isoWeekday() >= 6
                let baseHours = 0
                if (resData.hasShift) {
                  baseHours = resData.hours || 0
                } else if (!isWeekend) {
                  baseHours = resData.targetHours || 0
                }
                this.editingSickDays[index].hours = Math.round(baseHours * (pct / 100) * 100) / 100
                this.editingSickDays[index].shiftInfoTotalHours = resData.hours || 0
                if (resData.hasShift) {
                  this.editingSickDays[index].shiftInfo = {
                    name: resData.shiftName,
                    times: resData.times,
                  }
                } else {
                  this.editingSickDays[index].shiftInfo = null
                }
              } else {
                this.editingSickDays[index].hours = resData.targetHours
                this.editingSickDays[index].shiftInfo = null
              }
            }
          })
        })
      }
    },
  },
}
</script>

<style scoped lang="scss">
.transparent-table {
  background-color: rgba(255, 255, 255, 0.02) !important;
}
.border-light {
  border: 1px solid rgba(255, 255, 255, 0.05) !important;
}
.theme--light.v-data-table
  > .v-data-table__wrapper
  > table
  > tbody
  > tr:hover:not(.v-data-table__expanded__content):not(.v-data-table__empty-wrapper) {
  background: rgba(255, 255, 255, 0.05) !important;
}
.glass-card {
  background: rgba(30, 30, 30, 0.9) !important;
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.05);
}
.glass-toggle {
  background: rgba(255, 255, 255, 0.05) !important;
  border: 1px solid rgba(255, 255, 255, 0.05);
}
.toggle-btn {
  background: transparent !important;
  border: none !important;
  color: grey !important;
  font-size: 0.8rem !important;
  padding: 0 4px !important;
  min-width: 0 !important;
  letter-spacing: 0.5px;
  &.active-blue {
    color: #2196f3 !important;
    background: rgba(33, 150, 243, 0.1) !important;
  }
  &.active-orange {
    color: #ff9800 !important;
    background: rgba(255, 152, 0, 0.1) !important;
  }
  &.active-green {
    color: #00963f !important;
    background: rgba(0, 150, 63, 0.1) !important;
  }
  &.active-red {
    color: #f44336 !important;
    background: rgba(244, 67, 54, 0.1) !important;
  }
}
</style>
